import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, Search, Filter, Upload, Download, ChevronLeft, ChevronRight, 
  Check, X, Eye, Building, User, FileArchive, Download as DownloadIcon,
  Trash2
} from 'lucide-react';
import { usePilgrimGuideStore } from '../stores/pilgrimGuideStore';
import { useOrganizersStore } from '../stores/organizersStore';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { useLanguage } from '../contexts/LanguageContext';
import { collection, query, where, getDocs, doc, setDoc, updateDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { uploadFile } from '../utils/firebase';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { DocumentViewModal } from '../components/passportVisa/DocumentViewModal';
import { UploadDocumentModal } from '../components/passportVisa/UploadDocumentModal';
import { StatsPanel } from '../components/passportVisa/StatsPanel';
import { DocumentStatus } from '../types/document';
import { LoadingProgress } from '../components/common/LoadingProgress';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { downloadAllAsZip, DocumentToDownload } from '../utils/downloadAllAsZip';

export const PassportVisaPage = () => {
  const { translations } = useLanguage();
  const { links, loading, error, fetchLinks, initialized, progress } = usePilgrimGuideStore();
  const { organizers, fetchOrganizers } = useOrganizersStore();
  const [documentStatuses, setDocumentStatuses] = useState<Record<string, DocumentStatus>>({});
  const [loadingStatuses, setLoadingStatuses] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'complete' | 'missing'>('all');
  const [documentTypeFilter, setDocumentTypeFilter] = useState<'all' | 'passport' | 'visa'>('all');
  const [organizerFilter, setOrganizerFilter] = useState<string>('all');
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedPilgrimId, setSelectedPilgrimId] = useState<string | null>(null);
  const [selectedPilgrimName, setSelectedPilgrimName] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const itemsPerPage = 20;

  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  const userName = localStorage.getItem('userName');

  // English fallbacks for translations
  const t = {
    pageTitle: translations?.passportVisa?.pageTitle || 'Passport & Visa Documentation',
    pageSubtitle: translations?.passportVisa?.pageSubtitle || 'Manage passport and visa documents for pilgrims',
    downloadAllZip: translations?.passportVisa?.downloadAllZip || 'Download All as Zip',
    creatingZip: translations?.passportVisa?.creatingZip || 'Creating Zip...',
    exportStatus: translations?.passportVisa?.exportStatus || 'Export Status',
    exporting: translations?.passportVisa?.exporting || 'Exporting...',
    searchPlaceholder: translations?.passportVisa?.searchPlaceholder || 'Search by name, passport, guide, or organizer...',
    allOrganizers: translations?.passportVisa?.allOrganizers || 'All Organizers',
    allDocumentTypes: translations?.passportVisa?.allDocumentTypes || 'All Document Types',
    passportOnly: translations?.passportVisa?.passportOnly || 'Passport Only',
    visaOnly: translations?.passportVisa?.visaOnly || 'Visa Only',
    allStatuses: translations?.passportVisa?.allStatuses || 'All Statuses',
    complete: translations?.passportVisa?.complete || 'Complete',
    missing: translations?.passportVisa?.missing || 'Missing',
    activeFilters: translations?.passportVisa?.activeFilters || 'Active Filters:',
    search: translations?.passportVisa?.search || 'Search',
    organizer: translations?.passportVisa?.organizer || 'Organizer',
    status: translations?.passportVisa?.status || 'Status',
    documentType: translations?.passportVisa?.documentType || 'Document Type',
    loadingStatuses: translations?.passportVisa?.loadingStatuses || 'Loading document statuses...',
    documentsByCountry: translations?.passportVisa?.documentsByCountry || 'Documents by Country',
    documentsByOrganizer: translations?.passportVisa?.documentsByOrganizer || 'Documents by Organizer',
    noDataAvailable: translations?.passportVisa?.noDataAvailable || 'No data available',
    country: translations?.passportVisa?.country || 'Country',
    total: translations?.passportVisa?.total || 'Total',
    pilgrim: translations?.passportVisa?.pilgrim || 'Pilgrim',
    guideAndOrganizer: translations?.passportVisa?.guideAndOrganizer || 'Guide & Organizer',
    document: translations?.passportVisa?.document || 'Document',
    type: translations?.passportVisa?.type || 'Type',
    actions: translations?.passportVisa?.actions || 'Actions',
    uploaded: translations?.passportVisa?.uploaded || 'Uploaded',
    notUploaded: translations?.passportVisa?.notUploaded || 'Not Uploaded',
    viewDocument: translations?.passportVisa?.viewDocument || 'View Document',
    upload: translations?.passportVisa?.upload || 'Upload',
    view: translations?.passportVisa?.view || 'View',
    noDocumentsMatch: translations?.passportVisa?.noDocumentsMatch || 'No documents match your search criteria',
    noDocumentsAvailable: translations?.passportVisa?.noDocumentsAvailable || 'No documents available',
    showing: translations?.passportVisa?.showing || 'Showing',
    of: translations?.passportVisa?.of || 'of',
    documents: translations?.passportVisa?.documents || 'documents',
    loadingPilgrimData: translations?.passportVisa?.loadingPilgrimData || 'Loading pilgrim data...',
    previous: translations?.passportVisa?.previous || 'Previous',
    next: translations?.passportVisa?.next || 'Next',
    page: translations?.passportVisa?.page || 'Page',
    exportSuccess: translations?.passportVisa?.exportSuccess || 'Data exported successfully',
    exportError: translations?.passportVisa?.exportError || 'Failed to export data',
    downloadSuccess: translations?.passportVisa?.downloadSuccess || 'Documents downloaded successfully',
    downloadError: translations?.passportVisa?.downloadError || 'Failed to create zip file',
    uploadSuccess: (type: string) => translations?.passportVisa?.uploadSuccess?.replace('{type}', type) || `${type} uploaded successfully`,
    uploadError: translations?.passportVisa?.uploadError || 'Failed to upload document',
    failedToLoadStatuses: translations?.passportVisa?.failedToLoadStatuses || 'Failed to load document statuses',
    deleteSuccess: translations?.passportVisa?.deleteSuccess || 'Document deleted successfully',
    deleteError: translations?.passportVisa?.deleteError || 'Failed to delete document'
  };

  // Initialize links data if not already loaded
  useEffect(() => {
    if (!initialized) {
      fetchLinks();
    }
    
    // Fetch organizers for filter dropdown
    fetchOrganizers();
  }, [initialized, fetchLinks, fetchOrganizers]);

  // Fetch all document statuses from Firestore
  useEffect(() => {
    const fetchDocumentStatuses = async () => {
      if (loading) return;
      
      setLoadingStatuses(true);
      try {
        // Create query based on user role
        const documentsRef = collection(firestore, 'PilgrimDocuments');
        let q;
        
        if (userRole === 'organizer') {
          // Filter by organizer ID
          q = query(documentsRef, where('organizerId', '==', userId));
        } else if (userRole === 'guide') {
          // Filter by guide ID
          q = query(documentsRef, where('guideId', '==', userId));
        } else {
          // Admin gets all documents
          q = documentsRef;
        }
        
        const querySnapshot = await getDocs(q);
        const statuses: Record<string, DocumentStatus> = {};
        
        querySnapshot.forEach((doc) => {
          statuses[doc.id] = doc.data() as DocumentStatus;
        });
        
        setDocumentStatuses(statuses);
      } catch (error) {
        console.error('Error fetching document statuses:', error);
        toast.error(t.failedToLoadStatuses);
      } finally {
        setLoadingStatuses(false);
      }
    };

    fetchDocumentStatuses();
  }, [links, userRole, userId, loading, t.failedToLoadStatuses]);

  const handleViewDocument = (documentId: string) => {
    setSelectedDocumentId(documentId);
  };

  const handleOpenUploadModal = (pilgrimId: string, pilgrimName: string) => {
    setSelectedPilgrimId(pilgrimId);
    setSelectedPilgrimName(pilgrimName);
    setIsUploadModalOpen(true);
  };

  const handleUploadDocument = async (pilgrimId: string, documentType: 'passport' | 'visa', file: File) => {
    try {
      // Get pilgrim link
      const pilgrimLink = Object.values(links).find(link => link.pilgrimId === pilgrimId);
      if (!pilgrimLink) {
        throw new Error('Pilgrim link not found');
      }

      // Use passport number as filename
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || (file.type.includes('pdf') ? 'pdf' : 'jpg');
      const customFileName = `${pilgrimLink.passportNumber}.${fileExtension}`;
      
      // Upload file to storage with custom filename
      const fileData = await uploadFile(file, customFileName);
      
      // Create or update document status
      const documentId = `${pilgrimId}_${documentType}`;
      const documentRef = doc(firestore, 'PilgrimDocuments', documentId);
      const docSnap = await getDoc(documentRef);
      
      const documentData: DocumentStatus = {
        id: documentId,
        pilgrimId,
        pilgrimName: pilgrimLink.pilgrimName,
        passportNumber: pilgrimLink.passportNumber,
        documentType,
        fileUrl: fileData.url,
        fileName: customFileName,
        fileType: fileData.type,
        uploadedAt: Date.now(),
        uploadedBy: userId || 'unknown',
        uploadedByName: userName || 'Unknown User',
        organizerId: pilgrimLink.organizerId,
        organizerName: pilgrimLink.organizerName,
        guideId: pilgrimLink.guideId,
        guideName: pilgrimLink.guideName,
        status: 'complete',
        lastUpdatedAt: Date.now(),
        notes: ''
      };
      
      if (docSnap.exists()) {
        await updateDoc(documentRef, documentData);
      } else {
        await setDoc(documentRef, documentData);
      }
      
      // Update local state
      setDocumentStatuses(prev => ({
        ...prev,
        [documentId]: documentData
      }));
      
      toast.success(t.uploadSuccess(documentType.charAt(0).toUpperCase() + documentType.slice(1)));
      setIsUploadModalOpen(false);
    } catch (error: any) {
      console.error('Error uploading document:', error);
      toast.error(error.message || t.uploadError);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      // Remove from Firestore
      await deleteDoc(doc(firestore, 'PilgrimDocuments', documentId));
      
      // Update local state
      setDocumentStatuses(prev => {
        const newState = { ...prev };
        delete newState[documentId];
        return newState;
      });
      
      setSelectedDocumentId(null);
      toast.success(t.deleteSuccess);
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(t.deleteError);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      
      // Create export data for documents
      const exportData = [];
      
      // Gather all documents
      for (const pilgrim of filteredPilgrims) {
        const passportDocId = `${pilgrim.pilgrimId}_passport`;
        const visaDocId = `${pilgrim.pilgrimId}_visa`;
        const passportDoc = documentStatuses[passportDocId];
        const visaDoc = documentStatuses[visaDocId];
        
        // Add passport document if exists
        if (passportDoc) {
          exportData.push({
            'Pilgrim Name': pilgrim.pilgrimName,
            'Passport Number': pilgrim.passportNumber,
            'Country': pilgrim.residenceCountry,
            'Guide & Organizer': `${pilgrim.guideName} / ${pilgrim.organizerName}`,
            'Document Type': 'Passport',
            'Upload Date': passportDoc.uploadedAt ? new Date(passportDoc.uploadedAt).toLocaleDateString() : '-',
            'Uploaded By': passportDoc.uploadedByName || passportDoc.uploadedBy || 'Unknown',
            'Status': passportDoc.status === 'complete' ? 'Uploaded' : 'Missing'
          });
        } else {
          exportData.push({
            'Pilgrim Name': pilgrim.pilgrimName,
            'Passport Number': pilgrim.passportNumber,
            'Country': pilgrim.residenceCountry,
            'Guide & Organizer': `${pilgrim.guideName} / ${pilgrim.organizerName}`,
            'Document Type': 'Passport',
            'Upload Date': '-',
            'Uploaded By': '-',
            'Status': 'Missing'
          });
        }
        
        // Add visa document if exists
        if (visaDoc) {
          exportData.push({
            'Pilgrim Name': pilgrim.pilgrimName,
            'Passport Number': pilgrim.passportNumber,
            'Country': pilgrim.residenceCountry,
            'Guide & Organizer': `${pilgrim.guideName} / ${pilgrim.organizerName}`,
            'Document Type': 'Visa',
            'Upload Date': visaDoc.uploadedAt ? new Date(visaDoc.uploadedAt).toLocaleDateString() : '-',
            'Uploaded By': visaDoc.uploadedByName || visaDoc.uploadedBy || 'Unknown',
            'Status': visaDoc.status === 'complete' ? 'Uploaded' : 'Missing'
          });
        } else {
          exportData.push({
            'Pilgrim Name': pilgrim.pilgrimName,
            'Passport Number': pilgrim.passportNumber,
            'Country': pilgrim.residenceCountry,
            'Guide & Organizer': `${pilgrim.guideName} / ${pilgrim.organizerName}`,
            'Document Type': 'Visa',
            'Upload Date': '-',
            'Uploaded By': '-',
            'Status': 'Missing'
          });
        }
      }
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 30 }, // Pilgrim Name
        { wch: 20 }, // Passport Number
        { wch: 20 }, // Country
        { wch: 40 }, // Guide & Organizer
        { wch: 15 }, // Document Type
        { wch: 20 }, // Upload Date
        { wch: 25 }, // Uploaded By
        { wch: 15 }, // Status
      ];
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Documents Status');
      
      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `pilgrim-documents-status-${date}.xlsx`);
      
      toast.success(t.exportSuccess);
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error(t.exportError);
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadAllFiles = async () => {
    try {
      setIsDownloadingZip(true);
      
      // Prepare documents for download
      const documentsToDownload: DocumentToDownload[] = filteredDocuments.map(doc => ({
        pilgrimId: doc.pilgrimId,
        pilgrimName: doc.pilgrimName,
        documentId: doc.documentId,
        documentType: doc.documentType,
        status: doc.status,
        fileUrl: doc.fileUrl,
        passportNumber: doc.passportNumber
      }));
      
      // Download documents as zip
      await downloadAllAsZip({
        documents: documentsToDownload,
        documentStatuses,
        documentTypeFilter,
        translations: {
          downloadSuccess: t.downloadSuccess,
          downloadError: t.downloadError
        }
      });
      
    } catch (error) {
      console.error('Error downloading files:', error);
      toast.error(t.downloadError);
    } finally {
      setIsDownloadingZip(false);
    }
  };

  // Filter pilgrims based on search term and filters
  const filteredPilgrims = Object.values(links || {}).filter(link => {
    // Only include approved links
    if (link.status !== 'approved') {
      return false;
    }
    
    // Filter by organizer ID for organizer role
    if (userRole === 'organizer' && userId && link.organizerId !== userId) {
      return false;
    }
    
    // Filter by guide ID for guide role
    if (userRole === 'guide' && userId && link.guideId !== userId) {
      return false;
    }
    
    // Filter by selected organizer
    if (organizerFilter !== 'all' && link.organizerId !== organizerFilter) {
      return false;
    }
    
    const matchesSearch = !searchTerm || 
      link.pilgrimName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.passportNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.residenceCountry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.guideName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.organizerName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // Status filter
if (statusFilter !== 'all') {
  const passportDocId = `${link.pilgrimId}_passport`;
  const visaDocId = `${link.pilgrimId}_visa`;
  const passportDoc = documentStatuses[passportDocId];
  const visaDoc = documentStatuses[visaDocId];
  
  if (statusFilter === 'complete') {
    return passportDoc?.status === 'complete' || visaDoc?.status === 'complete';
  } else if (statusFilter === 'missing') {
    return (!passportDoc || passportDoc.status !== 'complete') && 
           (!visaDoc || visaDoc.status !== 'complete');
  }
}

// Document type filter
if (documentTypeFilter !== 'all') {
  const docId = `${link.pilgrimId}_${documentTypeFilter}`;
  const docStatus = documentStatuses[docId];

  return docStatus?.status === 'complete';
}

return true;

  });

  // Sort the filtered pilgrims by name
  const sortedPilgrims = [...filteredPilgrims].sort((a, b) => {
    if (a.pilgrimName && b.pilgrimName) {
      return a.pilgrimName.localeCompare(b.pilgrimName);
    }
    return 0;
  });

  // Calculate overall statistics
  const stats = {
    total: sortedPilgrims.length,
    passportUploaded: sortedPilgrims.filter(p => {
      const docId = `${p.pilgrimId}_passport`;
      return documentStatuses[docId]?.status === 'complete';
    }).length,
    visaUploaded: sortedPilgrims.filter(p => {
      const docId = `${p.pilgrimId}_visa`;
      return documentStatuses[docId]?.status === 'complete';
    }).length,
    complete: sortedPilgrims.filter(p => {
      const passportDocId = `${p.pilgrimId}_passport`;
      const visaDocId = `${p.pilgrimId}_visa`;
      return documentStatuses[passportDocId]?.status === 'complete' && 
             documentStatuses[visaDocId]?.status === 'complete';
    }).length,
    missing: sortedPilgrims.filter(p => {
      const passportDocId = `${p.pilgrimId}_passport`;
      const visaDocId = `${p.pilgrimId}_visa`;
      return (!documentStatuses[passportDocId] || documentStatuses[passportDocId]?.status !== 'complete') && 
             (!documentStatuses[visaDocId] || documentStatuses[visaDocId]?.status !== 'complete');
    }).length,
  };

  // Get all documents for the pilgrims
const allDocuments = useMemo(() => {
  const documents: {
    pilgrimId: string;
    pilgrimName: string;
    passportNumber: string;
    gender: string;
    residenceCountry: string;
    guideId: string;
    guideName: string;
    organizerId: string;
    organizerName: string;
    documentId: string;
    documentType: 'passport' | 'visa';
    status: 'complete' | 'missing';
    uploadedAt?: number;
    uploadedByName?: string;
    fileUrl?: string;
  }[] = [];

  for (const pilgrim of sortedPilgrims) {
    const passportDocId = `${pilgrim.pilgrimId}_passport`;
    const visaDocId = `${pilgrim.pilgrimId}_visa`;
    const passportDoc = documentStatuses[passportDocId];
    const visaDoc = documentStatuses[visaDocId];

    let selectedDoc;
    let selectedType: 'passport' | 'visa' = 'passport';

    if (passportDoc) {
      selectedDoc = passportDoc;
      selectedType = 'passport';
    } else if (visaDoc) {
      selectedDoc = visaDoc;
      selectedType = 'visa';
    }

    documents.push({
      pilgrimId: pilgrim.pilgrimId,
      pilgrimName: pilgrim.pilgrimName || '',
      passportNumber: pilgrim.passportNumber || '',
      gender: pilgrim.gender || '',
      residenceCountry: pilgrim.residenceCountry || '',
      guideId: pilgrim.guideId,
      guideName: pilgrim.guideName || '',
      organizerId: pilgrim.organizerId,
      organizerName: pilgrim.organizerName || '',
      documentId: selectedDoc?.id || `${pilgrim.pilgrimId}_${selectedType}`,
      documentType: selectedType,
      status: selectedDoc?.status === 'complete' ? 'complete' : 'missing',
      uploadedAt: selectedDoc?.uploadedAt,
      uploadedByName: selectedDoc?.uploadedByName || selectedDoc?.uploadedBy,
      fileUrl: selectedDoc?.fileUrl
    });
  }

  return documents;
}, [sortedPilgrims, documentStatuses]);
  
  // Filter documents based on type if needed
  const filteredDocuments = useMemo(() => {
    if (documentTypeFilter === 'all') {
      return allDocuments;
    }
    
    return allDocuments.filter(doc => doc.documentType === documentTypeFilter);
  }, [allDocuments, documentTypeFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredDocuments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDocuments = filteredDocuments.slice(startIndex, startIndex + itemsPerPage);

  // Get organizers for filter dropdown
  const uniqueOrganizers = useMemo(() => {
    if (userRole === 'organizer') return [];
    
    const orgSet = new Map();
    
    Object.values(links || {}).forEach(link => {
      if (link.status === 'approved' && link.organizerId && link.organizerName) {
        orgSet.set(link.organizerId, link.organizerName);
      }
    });
    
    return Array.from(orgSet.entries()).map(([id, name]) => ({ id, name }));
  }, [links, userRole]);

  if (loading && !initialized) {
    return (
      <div className="space-y-4">
        <LoadingSpinner />
        {progress.total > 0 && (
          <LoadingProgress
            progress={progress.current}
            total={progress.total}
            label={t.loadingPilgrimData}
          />
        )}
      </div>
    );
  }
  
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <FileText className="text-primary-500" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t.pageTitle}
            </h1>
            <p className="text-gray-500 mt-1">
              {t.pageSubtitle}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleDownloadAllFiles}
            disabled={isDownloadingZip || filteredPilgrims.length === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              isDownloadingZip || filteredPilgrims.length === 0 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
            } transition-colors`}
          >
            <FileArchive size={20} />
            <span>{isDownloadingZip ? t.creatingZip : t.downloadAllZip}</span>
          </button>

          <button
            onClick={handleExport}
            disabled={isExporting || filteredPilgrims.length === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              isExporting || filteredPilgrims.length === 0 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
            } transition-colors`}
          >
            <DownloadIcon size={20} />
            <span>{isExporting ? t.exporting : t.exportStatus}</span>
          </button>
        </div>
      </motion.div>

      {/* Stats Panel */}
      <StatsPanel stats={stats} />

   

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder={t.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          </div>

          {/* Organizer Filter - Only for admin */}
          {userRole === 'admin' && (
            <select
              value={organizerFilter}
              onChange={(e) => setOrganizerFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">{t.allOrganizers}</option>
              {uniqueOrganizers.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          )}

          {/* Document Type Filter */}
          <select
            value={documentTypeFilter}
            onChange={(e) => setDocumentTypeFilter(e.target.value as 'all' | 'passport' | 'visa')}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">{t.allDocumentTypes}</option>
            <option value="passport">{t.passportOnly}</option>
            <option value="visa">{t.visaOnly}</option>
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'complete' | 'missing')}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">{t.allStatuses}</option>
            <option value="complete">{t.complete}</option>
            <option value="missing">{t.missing}</option>
          </select>
        </div>

        {/* Active Filters */}
        {(searchTerm || statusFilter !== 'all' || documentTypeFilter !== 'all' || organizerFilter !== 'all') && (
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-primary-500" />
            <span className="text-sm text-gray-600">{t.activeFilters}</span>
            <div className="flex flex-wrap gap-2">
              {searchTerm && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  {t.search}: {searchTerm}
                </span>
              )}
              {organizerFilter !== 'all' && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded flex items-center gap-1">
                  <Building size={12} />
                  <span>{t.organizer}: {uniqueOrganizers.find(o => o.id === organizerFilter)?.name}</span>
                </span>
              )}
              {statusFilter !== 'all' && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  {t.status}: {statusFilter}
                </span>
              )}
              {documentTypeFilter !== 'all' && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  {t.documentType}: {documentTypeFilter}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Loading indicator for document statuses */}
      {loadingStatuses && (
        <div className="bg-white p-6 rounded-lg shadow-sm text-center">
          <LoadingSpinner />
          <p className="mt-2 text-gray-500">{t.loadingStatuses}</p>
        </div>
      )}

      {/* Documents List */}
      {!loadingStatuses && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.pilgrim}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.guideAndOrganizer}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.document}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.type}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.status}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.actions}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedDocuments.length > 0 ? (
                  paginatedDocuments.map((doc) => (
                    <tr key={doc.documentId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                              doc.gender === 'male' ? 'bg-blue-100 text-blue-600' : 'bg-pink-100 text-pink-600'
                            }`}>
                              {doc.pilgrimName?.charAt(0) || '?'}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{doc.pilgrimName}</div>
                            <div className="text-sm text-gray-500">{doc.passportNumber}</div>
                            <div className="text-xs text-gray-400">{doc.residenceCountry}</div>
                          </div>
                        </div>
                      </td>
                      
                      {/* Guide & Organizer Column (Combined) */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-2">
                          <div className="flex items-center">
                            <div className="p-2 rounded-full bg-primary-50">
                              <User size={14} className="text-primary-500" />
                            </div>
                            <div className="ml-2">
                              <div className="text-sm font-medium">{doc.guideName || 'N/A'}</div>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <div className="p-2 rounded-full bg-blue-50">
                              <Building size={14} className="text-blue-500" />
                            </div>
                            <div className="ml-2">
                              <div className="text-sm font-medium">{doc.organizerName || 'N/A'}</div>
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      {/* Document Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {doc.status === 'complete' ? (
                            <div className="flex items-center">
                              <Check className="mr-2 text-green-500" size={18} />
                              <div className="flex flex-col">
                                <span className="text-sm text-green-600 font-medium">{t.uploaded}</span>
                                <span className="text-xs text-gray-400">
                                  {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : '-'}
                                </span>
                                {doc.uploadedByName && (
                                  <span className="text-xs text-gray-500">
                                    {doc.uploadedByName}
                                  </span>
                                )}
                                {doc.fileUrl && (
                                  <button
                                    onClick={() => handleViewDocument(doc.documentId)}
                                    className="mt-1 text-xs text-primary-600 hover:text-primary-700"
                                  >
                                    {t.viewDocument}
                                  </button>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <X className="mr-2 text-red-500" size={18} />
                              <span className="text-sm text-red-600">{t.notUploaded}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      
                      {/* Document Type */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          doc.status === 'missing'
                            ? 'bg-gray-100 text-gray-800'
                            : doc.documentType === 'passport' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                        }`}>
                          {doc.status === 'missing' 
                            ? t.missing 
                            : doc.documentType.charAt(0).toUpperCase() + doc.documentType.slice(1)}
                        </span>
                      </td>
                      
                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {doc.status === 'complete' ? (
                          <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            {t.complete}
                          </span>
                        ) : (
                          <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            {t.missing}
                          </span>
                        )}
                      </td>
                      
                      {/* Actions */}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          {/* Upload button */}
                          {(userRole === 'admin' || 
                             (userRole === 'organizer' && userId === doc.organizerId) ||
                             (userRole === 'guide' && userId === doc.guideId)) && 
                             doc.status !== 'complete' && (
                            <button
                              onClick={() => handleOpenUploadModal(doc.pilgrimId, doc.pilgrimName)}
                              className="text-primary-600 hover:text-primary-900 flex items-center gap-1"
                              title="Upload Document"
                            >
                              <Upload size={16} />
                              <span>{t.upload}</span>
                            </button>
                          )}

                          {/* View document button */}
                          {doc.status === 'complete' && (
                            <button
                              onClick={() => handleViewDocument(doc.documentId)}
                              className="text-blue-600 hover:text-blue-900 flex items-center gap-1 ml-3"
                            >
                              <Eye size={16} />
                              <span>{t.view}</span>
                            </button>
                          )}

                          {/* Delete document button */}
                          {doc.status === 'complete' && 
                           (userRole === 'admin' || 
                            (userRole === 'organizer' && userId === doc.organizerId) ||
                            (userRole === 'guide' && userId === doc.guideId)) && (
                            <button
                              onClick={() => handleDeleteDocument(doc.documentId)}
                              className="text-red-600 hover:text-red-900 flex items-center gap-1 ml-3"
                            >
                              <Trash2 size={16} />
                              <span>{translations?.common?.delete || 'Delete'}</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      {searchTerm || statusFilter !== 'all' || documentTypeFilter !== 'all' || organizerFilter !== 'all'
                        ? t.noDocumentsMatch
                        : t.noDocumentsAvailable}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredDocuments.length > itemsPerPage && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                    currentPage === 1 ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {t.previous}
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`relative ml-3 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                    currentPage === totalPages ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {t.next}
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    {t.showing} <span className="font-medium">{startIndex + 1}</span> {t.of}{' '}
                    <span className="font-medium">
                      {Math.min(startIndex + itemsPerPage, filteredDocuments.length)}
                    </span>{' '}
                    {t.of} <span className="font-medium">{filteredDocuments.length}</span> {t.documents}
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">{t.previous}</span>
                      <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                    </button>
                    
                    {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
                      let pageNumber;
                      
                      if (totalPages <= 5) {
                        pageNumber = i + 1;
                      } else if (currentPage <= 3) {
                        pageNumber = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNumber = totalPages - 4 + i;
                      } else {
                        pageNumber = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium ${
                            currentPage === pageNumber
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">{t.next}</span>
                      <ChevronRight className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Document View Modal */}
      {selectedDocumentId && documentStatuses[selectedDocumentId] && (
        <DocumentViewModal
          isOpen={!!selectedDocumentId}
          onClose={() => setSelectedDocumentId(null)}
          documentData={documentStatuses[selectedDocumentId]}
          onDelete={() => {
            const docId = selectedDocumentId;
            setSelectedDocumentId(null);
            handleDeleteDocument(docId);
          }}
        />
      )}

      {/* Upload Document Modal */}
      {selectedPilgrimId && (
        <UploadDocumentModal
          isOpen={isUploadModalOpen}
          onClose={() => {
            setIsUploadModalOpen(false);
            setSelectedPilgrimId(null);
          }}
          pilgrimId={selectedPilgrimId}
          pilgrimName={selectedPilgrimName}
          onUpload={handleUploadDocument}
        />
      )}
    </div>
  );
};